/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import {
  Search, Send, Loader2, Target, Brain, TrendingUp, AlertTriangle,
  ChevronRight, ThumbsDown, Settings2, Eye,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function adminHeaders() {
  const k = localStorage.getItem('admin_key') || '';
  return { 'X-Admin-Key': k };
}

// ========= Factor breakdown (left column) =========
function FactorBar({ f }) {
  const colorByDir = {
    base: 'bg-sky-500/70',
    positive: 'bg-emerald-500/80',
    negative: 'bg-rose-500/80',
    qualitative: 'bg-amber-500/80',
  };
  const c = colorByDir[f.direction] || 'bg-zinc-500/70';
  const magnitude = Math.min(100, Math.abs(Number(f.value) || 0) * 4);
  return (
    <div className="py-2 border-b border-white/5 last:border-0">
      <div className="flex items-center justify-between">
        <div className="text-sm text-white font-medium">{f.label}</div>
        <div className={`text-sm font-mono ${f.direction === 'negative' ? 'text-rose-300' : 'text-emerald-300'}`}>
          {f.direction === 'negative' ? '' : (f.direction === 'positive' ? '+' : '')}
          {f.value}{f.unit}
        </div>
      </div>
      <div className="h-1.5 bg-zinc-800 rounded-full mt-2 overflow-hidden">
        <div className={`h-full ${c} rounded-full transition-all`} style={{ width: `${magnitude}%` }} />
      </div>
      <div className="text-[11px] text-zinc-500 mt-1">{f.source}{f.detail ? ` · ${f.detail}` : ''}</div>
    </div>
  );
}

// ========= Q&A chat (right column) =========
function ChatPanel({ pickId, onCountChange }) {
  const [history, setHistory] = useState([]);
  const [question, setQuestion] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/picks/${pickId}/qa_history`, { headers: adminHeaders() });
      if (r.ok) {
        const d = await r.json();
        setHistory(d.items || []);
        onCountChange?.(d.count || 0);
        // reuse last session if any
        if (d.items?.length) setSessionId(d.items[d.items.length - 1].session_id);
      }
    } catch (_e) { /* silent */ }
  }, [pickId]);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history]);

  async function send() {
    const q = question.trim();
    if (!q || sending) return;
    setSending(true);
    const temp = { question: q, answer: null, pending: true };
    setHistory((h) => [...h, temp]);
    setQuestion('');
    try {
      const r = await fetch(`${API_URL}/api/picks/${pickId}/ask`, {
        method: 'POST',
        headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q, session_id: sessionId }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setSessionId(d.session_id);
      setHistory((h) => h.map((it) => it === temp ? { question: q, answer: d.answer, created_at: new Date().toISOString(), latency_ms: d.latency_ms } : it));
    } catch (e) {
      toast.error(`Error Claude: ${e.message}`);
      setHistory((h) => h.filter((it) => it !== temp));
    }
    finally { setSending(false); }
  }

  return (
    <div className="flex flex-col h-full" data-testid="pick-qa-panel">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 min-h-[320px] bg-zinc-950/50 rounded-lg border border-white/5">
        {history.length === 0 && (
          <div className="text-zinc-500 text-sm text-center py-8">
            Hazle preguntas al analista sobre este pick.<br />
            Ejemplos: "¿Por qué ha pesado tanto la motivación?" · "¿Cuál es el histórico H2H?"
          </div>
        )}
        {history.map((t, i) => (
          <div key={i}>
            <div className="text-xs text-sky-300 font-semibold mb-1">Tú</div>
            <div className="text-sm text-zinc-200 bg-sky-500/10 border border-sky-500/20 rounded-lg px-3 py-2 mb-2">{t.question}</div>
            <div className="text-xs text-emerald-300 font-semibold mb-1">Analista</div>
            {t.pending ? (
              <div className="flex items-center gap-2 text-zinc-400 text-sm"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analizando…</div>
            ) : (
              <div className="text-sm text-zinc-200 bg-emerald-500/5 border border-emerald-500/15 rounded-lg px-3 py-2 whitespace-pre-wrap">{t.answer}</div>
            )}
            {t.latency_ms && <div className="text-[10px] text-zinc-600 mt-1">{Math.round(t.latency_ms)}ms</div>}
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <Input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Pregunta al analista sobre este pick…"
          className="bg-zinc-900 border-white/10 text-white"
          data-testid="pick-qa-input"
          disabled={sending}
        />
        <Button onClick={send} disabled={sending || !question.trim()} data-testid="pick-qa-send"
                className="bg-emerald-600 hover:bg-emerald-500 text-white">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}

// ========= Footer feedback + weights shortcut =========
function FeedbackFooter({ pickId, onOpenWeights }) {
  const [factorKey, setFactorKey] = useState('motivation');
  const [direction, setDirection] = useState('too_high');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit() {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/picks/${pickId}/feedback`, {
        method: 'POST',
        headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ factor_key: factorKey, direction, notes }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Feedback registrado');
      setNotes('');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  }

  const factors = ['motivation', 'importance', 'value_edge', 'news', 'form', 'h2h', 'injuries', 'market_choice'];
  const directions = [
    { v: 'too_high', l: 'Peso muy alto' },
    { v: 'too_low', l: 'Peso muy bajo' },
    { v: 'wrong_sign', l: 'Dirección errónea' },
    { v: 'missing_factor', l: 'Factor ausente' },
    { v: 'other', l: 'Otro' },
  ];

  return (
    <div className="border-t border-white/10 pt-3 mt-3 flex flex-wrap items-center gap-2" data-testid="pick-feedback-footer">
      <ThumbsDown className="w-4 h-4 text-rose-300" />
      <select value={factorKey} onChange={(e) => setFactorKey(e.target.value)}
              className="bg-zinc-900 border border-white/10 text-white text-xs rounded px-2 py-1"
              data-testid="feedback-factor">
        {factors.map((f) => <option key={f} value={f}>{f}</option>)}
      </select>
      <select value={direction} onChange={(e) => setDirection(e.target.value)}
              className="bg-zinc-900 border border-white/10 text-white text-xs rounded px-2 py-1"
              data-testid="feedback-direction">
        {directions.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
      </select>
      <Input value={notes} onChange={(e) => setNotes(e.target.value)}
             placeholder="Nota opcional…"
             className="bg-zinc-900 border-white/10 text-white text-xs h-8 flex-1 min-w-[160px]" />
      <Button size="sm" onClick={submit} disabled={saving}
              className="h-8 text-xs bg-rose-600 hover:bg-rose-500 text-white"
              data-testid="feedback-submit">
        Registrar
      </Button>
      <Button size="sm" variant="outline" onClick={onOpenWeights}
              className="h-8 text-xs border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              data-testid="open-weights-btn">
        <Settings2 className="w-3.5 h-3.5 mr-1" /> Ajustar pesos del engine
      </Button>
    </div>
  );
}

// ========= Weights tuner (secondary modal) =========
function WeightsTunerDialog({ open, onClose }) {
  const [weights, setWeights] = useState(null);
  const [proposed, setProposed] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) { setPreview(null); return; }
    (async () => {
      const r = await fetch(`${API_URL}/api/admin/engine/weights`, { headers: adminHeaders() });
      if (r.ok) {
        const d = await r.json();
        setWeights(d);
        setProposed({ ...d });
      }
    })();
  }, [open]);

  async function runPreview() {
    if (!proposed) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/engine/weights/preview`, {
        method: 'POST',
        headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...proposed, days: 7 }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      setPreview(d);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }
  async function applyWeights() {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/engine/weights`, {
        method: 'PATCH',
        headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...proposed, confirm: true }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Pesos actualizados. Se aplicarán al próximo ciclo de generación.');
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }

  const sliders = [
    { k: 'motivation_weight_max', min: 0, max: 30, step: 0.5, label: 'Motivación — cap máx (pp)', help: 'Tope del ajuste por motivación sobre 1X2. Bajarlo reduce el peso de "lo que se juegan".' },
    { k: 'motivation_draw_damp_thresh', min: 0, max: 10, step: 0.5, label: 'Motivación — umbral empate (pp)', help: 'Diferencia mínima entre equipos para reducir el empate.' },
    { k: 'importance_multiplier', min: 0, max: 2, step: 0.05, label: 'Importancia del partido ×', help: 'Multiplica la intensidad × categoría. 1.0=actual, 0.7=–30%.' },
    { k: 'value_edge_min_pp', min: 0, max: 20, step: 0.5, label: 'Value edge mínimo (pp)', help: 'Edge mínimo para que un pick se publique.' },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-zinc-950 border-white/10 text-white max-w-2xl" data-testid="weights-tuner-dialog">
        <DialogHeader><DialogTitle>Ajustar pesos del engine</DialogTitle></DialogHeader>
        {!weights ? <div className="py-4 text-zinc-400">Cargando…</div> : (
          <div className="space-y-4">
            {sliders.map((s) => (
              <div key={s.k}>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white font-medium">{s.label}</span>
                  <span className="text-sm font-mono text-amber-300">{Number(proposed?.[s.k] ?? 0).toFixed(2)}</span>
                </div>
                <input type="range" min={s.min} max={s.max} step={s.step}
                       value={proposed?.[s.k] ?? 0}
                       onChange={(e) => { setProposed({ ...proposed, [s.k]: parseFloat(e.target.value) }); setPreview(null); }}
                       className="w-full"
                       data-testid={`weight-${s.k}`} />
                <div className="text-[11px] text-zinc-500">Actual: <span className="font-mono text-zinc-400">{weights[s.k].toFixed(2)}</span> · {s.help}</div>
              </div>
            ))}
            <div className="border-t border-white/10 pt-3 flex items-center gap-2">
              <Button variant="outline" onClick={runPreview} disabled={loading}
                      className="border-sky-500/40 text-sky-300 hover:bg-sky-500/10"
                      data-testid="weights-preview-btn">
                <Eye className="w-4 h-4 mr-1" /> Ver impacto
              </Button>
              <Button onClick={applyWeights} disabled={loading || !preview}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white"
                      data-testid="weights-apply-btn">
                Aplicar (requiere preview)
              </Button>
            </div>
            {preview && (
              <Card className="bg-zinc-900/60 border-amber-500/20" data-testid="weights-preview-result">
                <CardContent className="p-3 text-sm space-y-1">
                  <div className="text-amber-300 font-semibold">Impacto estimado ({preview.total_picks_scanned} picks últimos {preview.days_analyzed}d)</div>
                  <div className="text-xs text-zinc-300">• {preview.impact.would_filter_out_count} picks se filtrarían fuera</div>
                  <div className="text-xs text-zinc-300">• {preview.impact.motivation_would_change_count} picks cambiarían su ajuste por motivación</div>
                  <div className="text-xs text-zinc-300">• {preview.impact.new_qualifies_count} picks nuevos pasarían el filtro</div>
                  <div className="text-xs text-zinc-500">• {preview.impact.untouched_count} sin cambios</div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ========= MAIN DIALOG =========
export default function PickAnalysisDialog({ open, onClose, pickId, matchLabel }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qaCount, setQaCount] = useState(0);
  const [weightsOpen, setWeightsOpen] = useState(false);

  useEffect(() => {
    if (!open || !pickId) return;
    setLoading(true);
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/picks/${pickId}/analysis`, { headers: adminHeaders() });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setAnalysis(await r.json());
      } catch (e) { toast.error(`Analysis: ${e.message}`); setAnalysis(null); }
      finally { setLoading(false); }
    })();
  }, [open, pickId]);

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="pick-analysis-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-emerald-400" />
              ¿Por qué este pick? <span className="text-zinc-400 font-normal">· {matchLabel}</span>
              {qaCount > 0 && <Badge variant="outline" className="border-sky-500/40 text-sky-300">{qaCount} Q&A</Badge>}
            </DialogTitle>
          </DialogHeader>

          {loading && <div className="py-8 text-zinc-400 text-center"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>}
          {!loading && !analysis && <div className="py-8 text-rose-300 text-center">No se pudo cargar el análisis.</div>}
          {analysis && (
            <div className="grid md:grid-cols-2 gap-4 overflow-y-auto pr-1">
              {/* LEFT — factor breakdown */}
              <div className="space-y-3">
                <Card className="bg-zinc-900/50 border-white/5">
                  <CardContent className="p-3">
                    <div className="text-xs uppercase text-zinc-400 mb-2 flex items-center gap-1"><Target className="w-3 h-3" /> Resumen del pick</div>
                    <div className="text-xl font-bold">{analysis.prediction} @ {analysis.odds}</div>
                    <div className="text-sm text-zinc-400">{analysis.market} · {analysis.league}</div>
                    <div className="flex items-center gap-3 mt-2 text-xs">
                      <span className="text-emerald-300">Prob. modelo: <span className="font-mono text-base">{analysis.our_probability}%</span></span>
                      {analysis.implied_probability !== null && <span className="text-zinc-400">Implícita: <span className="font-mono">{analysis.implied_probability}%</span></span>}
                      <span className={analysis.value_edge_pp > 0 ? 'text-emerald-300' : 'text-rose-300'}>Edge: <span className="font-mono">{analysis.value_edge_pp > 0 ? '+' : ''}{analysis.value_edge_pp}pp</span></span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-zinc-900/50 border-white/5">
                  <CardContent className="p-3">
                    <div className="text-xs uppercase text-zinc-400 mb-1 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Factores considerados</div>
                    {analysis.factors.map((f, i) => <FactorBar key={i} f={f} />)}
                  </CardContent>
                </Card>

                {analysis.motivation?.applied && (
                  <Card className="bg-amber-500/5 border-amber-500/20">
                    <CardContent className="p-3 text-xs">
                      <div className="text-amber-300 font-semibold mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Detalle motivación</div>
                      <div className="text-zinc-300 space-y-0.5">
                        <div>Home <span className="font-mono">{analysis.home}</span>: cat <span className="font-mono">{analysis.motivation.home_category}</span> · intensity <span className="font-mono">{analysis.motivation.home_intensity}</span></div>
                        <div>Away <span className="font-mono">{analysis.away}</span>: cat <span className="font-mono">{analysis.motivation.away_category}</span> · intensity <span className="font-mono">{analysis.motivation.away_intensity}</span></div>
                        <div>Deltas: H <span className={`font-mono ${analysis.motivation.deltas_pp.home >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{analysis.motivation.deltas_pp.home}pp</span> · X <span className="font-mono">{analysis.motivation.deltas_pp.draw}pp</span> · A <span className={`font-mono ${analysis.motivation.deltas_pp.away >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{analysis.motivation.deltas_pp.away}pp</span></div>
                        {analysis.motivation.reasoning && <div className="text-zinc-400 mt-1 italic">"{analysis.motivation.reasoning}"</div>}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* RIGHT — chat */}
              <ChatPanel pickId={pickId} onCountChange={setQaCount} />
            </div>
          )}

          {/* FOOTER — feedback + weights shortcut */}
          {analysis && <FeedbackFooter pickId={pickId} onOpenWeights={() => setWeightsOpen(true)} />}
        </DialogContent>
      </Dialog>

      <WeightsTunerDialog open={weightsOpen} onClose={() => setWeightsOpen(false)} />
    </>
  );
}

// ========= Trigger button — drop into any card =========
export function PickAnalysisTriggerBtn({ pickId, matchLabel, className = '' }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        size="sm" variant="outline"
        className={`border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 ${className}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={`pick-analysis-btn-${pickId}`}
      >
        <Search className="w-3.5 h-3.5 mr-1" /> ¿Por qué este pick?
      </Button>
      {open && <PickAnalysisDialog open={open} onClose={() => setOpen(false)} pickId={pickId} matchLabel={matchLabel} />}
    </>
  );
}
